import { parseISOStrictOr } from "../helpers/date";
import { getItem, setItem } from "../helpers/localStorage";
import type { AuthenticatedAccountUser } from "../types/management-auth";

// The accounts a user has recently been in, most recent first. Stored as ids
// without the P/T prefix, so an account's test and production modes share an
// entry like they do in the account switcher. Kept per user, as several users
// can sign in on the same browser.
//
// One more than the switcher shows, as it leaves out the current account.
const MAX_RECENT_ACCOUNTS = 6;

const storageKey = (userId: string) => `recent_accounts_${userId}`;

const toBaseId = (accountId: string) => accountId.substring(1);

const readStored = (userId: string): string[] | undefined => {
    const stored = getItem(storageKey(userId));
    if (!stored) {
        return undefined;
    }
    try {
        const ids: unknown = JSON.parse(stored);
        return Array.isArray(ids)
            ? ids.filter((id): id is string => typeof id === "string")
            : undefined;
    } catch {
        return undefined;
    }
};

// Before anything is stored, start from the accounts last signed in to
const fromLastSeen = (accountUser: AuthenticatedAccountUser) => {
    const lastSeen = (account: AuthenticatedAccountUser["accounts"][number]) =>
        parseISOStrictOr(account.user.last_seen_at, new Date(0)).getTime();
    const ids = [...accountUser.accounts]
        .filter((account) => account.user.last_seen_at)
        .sort((a, b) => lastSeen(b) - lastSeen(a))
        .map((account) => toBaseId(account.account_id));
    return [...new Set(ids)].slice(0, MAX_RECENT_ACCOUNTS);
};

export const getRecentAccountIds = (
    accountUser: AuthenticatedAccountUser,
): string[] =>
    (accountUser.id && readStored(accountUser.id)) || fromLastSeen(accountUser);

// Called whenever the app shows an account, however it was reached
export const recordRecentAccount = (
    accountUser: AuthenticatedAccountUser,
    accountId: string,
) => {
    if (!accountUser.id) {
        return;
    }
    const baseId = toBaseId(accountId);
    const ids = getRecentAccountIds(accountUser).filter((id) => id !== baseId);
    setItem(
        storageKey(accountUser.id),
        JSON.stringify([baseId, ...ids].slice(0, MAX_RECENT_ACCOUNTS)),
    );
};
